import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  FieldCheckbox,
  FieldTextInput,
  ValidationError,
  CircularProgressLoader,
} from '../../components';
import '../../components/LayoutWrapperMain/LayoutWrapperMain.global.scss';
import '../AddNewRoles/addnewroles.global.scss';

enum ButtonTypes {
  'button',
  'submit',
  'reset',
  undefined
}

interface Props {
  handleSubmit: any;
  handleSaveAsDraft: any;
  loader: any;
  roles: any;
}

interface ModuleDef {
  label: string;
  key: string;
}

interface GroupDef {
  title: string;
  modules: ModuleDef[];
}

const PERMISSION_ACTIONS = ['add', 'edit', 'delete'] as const;

const PERMISSION_GROUPS: GroupDef[] = [
  {
    title: 'Users & Access',
    modules: [
      { label: 'User Management', key: 'usermgmt' },
      { label: 'Subscribed Users', key: 'subscribedusers' },
      { label: 'Reported User Management', key: 'reportedusermgmt' },
      { label: 'Role Management', key: 'rolemgmt' },
      { label: 'Sub-Admin Management', key: 'subadminmgmt' },
    ],
  },
  {
    title: 'Content & Community',
    modules: [
      { label: 'Channel Management', key: 'channelmgmt' },
      { label: 'Live Streaming', key: 'livestreaming' },
      { label: 'Event Management', key: 'eventmgmt' },
      { label: 'Domes Management', key: 'categories' },
      { label: 'Sub Domes Management', key: 'subcategorymgmt' },
      { label: 'Content Management', key: 'contentmgmt' },
      { label: 'Landing Page', key: 'landing' },
      { label: 'Thea', key: 'thea' },
      { label: 'ENTER VICE', key: 'entervice' },
      { label: 'Special Posts', key: 'specialposts' },
    ],
  },
  {
    title: 'Commerce & Payments',
    modules: [
      { label: 'Subscription Plan Management', key: 'subscriptionplanmgmt' },
      { label: 'Service Provider Management', key: 'serviceprovidemgmt' },
      { label: 'Purchase / E-commerce', key: 'purchaseecommerce' },
      { label: 'Campaign Management', key: 'campaignmgmt' },
      { label: 'Promotions & Advertising', key: 'promotionsadver' },
      { label: 'Seller / Brand Management', key: 'sellerbrandmgmt' },
      { label: 'Payment Management', key: 'paymentmgmt' },
      { label: 'Coin Activity Management', key: 'coinactivitymgmt' },
      { label: 'Commission Management', key: 'commissionmgmt' },
      { label: 'Reward Points', key: 'rewardpoints' },
    ],
  },
  {
    title: 'Support & Operations',
    modules: [
      { label: 'Issues Reported', key: 'issuesreported' },
      { label: 'Version Management', key: 'versionmanagement' },
      { label: 'Reminder Emails', key: 'reminderemails' },
      { label: 'Notification Management', key: 'notificationmmt' },
      { label: 'Support', key: 'support' },
      { label: 'Feedback', key: 'feedback' },
      { label: 'Report Management', key: 'reportmgmt' },
    ],
  },
];

const ALL_MODULES: ModuleDef[] = PERMISSION_GROUPS.flatMap((g) => g.modules);

const fieldName = (moduleKey: string, action: string) => `${moduleKey}_${action}`;

const EditRolesForm: React.FC<Props> = (props: Props) => {
  const { handleSubmit, handleSaveAsDraft, loader, roles } = props;

  const formProps = useForm();
  const { setValue, watch } = formProps;

  useEffect(() => {
    formProps.reset(roles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles]);

  const setModuleRow = (moduleKey: string, value: boolean) => {
    PERMISSION_ACTIONS.forEach((action) => {
      setValue(fieldName(moduleKey, action), value, { shouldDirty: true });
    });
  };

  const setGroup = (group: GroupDef, value: boolean) => {
    group.modules.forEach((mod) => setModuleRow(mod.key, value));
  };

  const setAll = (value: boolean) => {
    ALL_MODULES.forEach((mod) => setModuleRow(mod.key, value));
  };

  const isModuleFullyChecked = (moduleKey: string) =>
    PERMISSION_ACTIONS.every((action) => !!watch(fieldName(moduleKey, action)));

  const isGroupFullyChecked = (group: GroupDef) =>
    group.modules.every((mod) => isModuleFullyChecked(mod.key));

  const isAllChecked = ALL_MODULES.every((mod) => isModuleFullyChecked(mod.key));

  return (
    <>
      <div className="cardBg addDetailsPage">
        <form className="form" onSubmit={formProps.handleSubmit(handleSubmit)}>
          <h2 className="sign_text">Edit Roles</h2>
          <div className="flex_row">
            <div className="flex_col_sm_4">
              <FieldTextInput
                id="roleName"
                name="roleName"
                formName="newRole"
                className="textInput"
                type="text"
                formProps={formProps}
                label="*Role Name"
              />
            </div>
          </div>

          <div className="permissionsHeader">
            <h3 className="sign_text_s">Permissions</h3>
            <label className="selectAllToggle">
              <input
                type="checkbox"
                checked={isAllChecked}
                onChange={(e) => setAll(e.target.checked)}
              />
              Grant all permissions
            </label>
          </div>

          {PERMISSION_GROUPS.map((group) => (
            <div className="permissionGroup" key={group.title}>
              <div className="permissionGroupHead">
                <span className="permissionGroupTitle">{group.title}</span>
                <div className="permissionGroupActions">
                  <button
                    type="button"
                    className="linkAction"
                    onClick={() => setGroup(group, true)}
                  >
                    Select all
                  </button>
                  <span className="sep">|</span>
                  <button
                    type="button"
                    className="linkAction"
                    onClick={() => setGroup(group, false)}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <table className="dataTableCover permissionsTable">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Add</th>
                    <th>Edit</th>
                    <th>Delete</th>
                    <th>Full access</th>
                  </tr>
                </thead>
                <tbody>
                  {group.modules.map((mod) => (
                    <tr key={mod.key}>
                      <td>{mod.label}</td>
                      {PERMISSION_ACTIONS.map((action) => (
                        <td key={action}>
                          <FieldCheckbox
                            id={fieldName(mod.key, action)}
                            name={fieldName(mod.key, action)}
                            formProps={formProps}
                          />
                        </td>
                      ))}
                      <td>
                        <input
                          type="checkbox"
                          className="rowSelectAll"
                          aria-label={`Full access to ${mod.label}`}
                          checked={isModuleFullyChecked(mod.key)}
                          onChange={(e) => setModuleRow(mod.key, e.target.checked)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {isGroupFullyChecked(group) && (
                <span className="groupHint">All modules in this group are fully enabled.</span>
              )}
            </div>
          ))}

          {Object.keys(formProps.formState.errors).length > 0 && (
            <ValidationError
              className="validation_error"
              error={formProps.formState.errors[
                Object.keys(formProps.formState.errors)[0]
              ]?.message?.toString()}
            />
          )}
          {loader ? (
            <CircularProgressLoader />
          ) : (
            <div className="buttonWrapper">
              <Button onClick={(e: any) => handleSaveAsDraft(e, formProps.getValues())}>
                Save as Draft
              </Button>
              <Button buttonType={ButtonTypes.submit}>Update Role</Button>
            </div>
          )}
        </form>
      </div>
    </>
  );
};

export default EditRolesForm;
